import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import {
  RouterProvider,
} from 'react-router';
import {
  ApolloProvider,
} from '@apollo/client';
import io from 'socket.io-client';
import { makeBrowserRouter, makeHashRouter, makeMemoryRouter } from './router';
import makeApolloClient from './utils/apollo-client';
import run from './utils/run';
import getSocketClient from './utils/socket-client';
import './index.scss';
import { until } from './utils/defer';
import { getURLParams, getParams } from './utils/params';
import PortalApp from './PortalApp';


export function getRouter(mode) {
  if (mode === 'mfe') {
    return makeHashRouter();
  }
  if (mode === 'spa') {
    return makeBrowserRouter();
  }
  return makeMemoryRouter();
}

export function getClient(router, params, $location) {
  const options = {
    headers: {
      'x-mfe-dre': encodeURIComponent(JSON.stringify(params || {})),
    },
  };
  return makeApolloClient(router, options, $location);
}

export function getApplication(mode, params, $location) {
  const router = getRouter(mode);
  const client = getClient(router, params, $location);
  return (
    <PortalApp>
      <ApolloProvider client={client}>
        <RouterProvider router={router} />
      </ApolloProvider>
    </PortalApp>
  );
}

export function getMfeView(params, $location) {
  let url;
  const { view } = params;
  // NOTE: we may enhance this to be more robust in the future
  if (view === 'qrcode' || $location.href.match(/#\/qrcode/)) {
    url = `${$location.pathname}${$location.search}#/qrcode`;
  } else if (!$location.href.match(/#\/store\/.*$/)) {
    const { storeId, orderNumber } = params;
    url = `${$location.pathname}${$location.search}#/store/${storeId}/exchange/${orderNumber}`;
  }
  return url;
}

export function getSpaView(params, $location) {
  let url;
  const { view } = params;
  // NOTE: we may enhance this to be more robust in the future
  if (view === 'qrcode' || $location.pathname.match(/^\/qrcode/)) {
    url = `/qrcode${$location.search}`;
  } else if (!$location.pathname.match(/^\/store\//)) {
    const { storeId, orderNumber } = params;
    url = `/store/${storeId}/exchange/${orderNumber}${$location.search}`;
  }
  return url;
}

export function getHistoryURL(mode, params, $location) {
  let url;
  if (mode === 'mfe') {
    url = getMfeView(params, $location);
  } else if (mode === 'spa') {
    url = getSpaView(params, $location);
  }
  return url;
}

export function updateHistory(url, $document, $history) {
  try {
    if (url) {
      $history.replaceState(
        null,
        $document.title,
        url,
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

export function render(mode, $document, containerID, app) {
  const container = $document.getElementById(containerID);
  if (mode === 'mfe') {
    hydrateRoot(container, app);
  } else {
    const root = createRoot(container);
    root.render(app);
  }
  container.style.display = 'block';
}

function updateLangIdOnSession($sessionStorage, langId) {
  if ($sessionStorage.getItem('langId') && $sessionStorage.getItem('langId') !== langId) {
    $sessionStorage.clear();
  }
  $sessionStorage.setItem('langId', langId);
}

export function start(mode, containerID, $window) {
  const {
    location: $location,
    document: $document,
    history: $history,
    sessionStorage: $sessionStorage,
  } = $window;
  const params = getParams($window);
  updateLangIdOnSession($sessionStorage, params.langId);
  const url = getHistoryURL(mode, params, $location);
  updateHistory(url, $document, $history);
  const app = getApplication(mode, params, $location);
  render(mode, $document, containerID, app);
}

export function updateDre20Params($window, params) {
  if (typeof params === 'object' && params !== null) {
    $window.dre20params = { ...$window.dre20params, ...params };
  }
}

export function bootstrap(switchValue, containerID, button, $window, mode = 'mfe') {
  const {
    document: $document,
    location: $location,
  } = $window;

  const container = $document.getElementById(containerID);
  if (!container) {
    return;
  }

  container.style.display = 'none';

  if (!switchValue) {
    return;
  }

  // Methods For Online Orders Flow - Make Legacy Container Visible
  const legOrdHistWrapper = $document.querySelector('.order-history__wrapper[data-returns-flow="OD"]');
  const params = getURLParams($location);
  if (params.requestFrom === 'startmyreturn' && legOrdHistWrapper) {
    // Show the Legacy Container
    legOrdHistWrapper.style.display = 'block';
  }

  // look for the legacy button in the DOM and either attach a click handler to start the hydration
  // process or if it is not present just start the hydration process
  const startHandler = (event) => {
    event.preventDefault();
    $window.removeEventListener('dre20:start', startHandler);
    updateDre20Params($window, event.detail);
    const target = getTarget(getParams($window));
    start(mode, target, $window);
    // Methods For Online Orders Flow - Make Legacy Container Hidden
    if (params.requestFrom === 'startmyreturn' && legOrdHistWrapper) {
      // Hide the Legacy Container if entering into DRE 2.0
      legOrdHistWrapper.style.display = 'none';
    }
  };

  $window.addEventListener('dre20:start', startHandler);
  if (button) {
    button.addEventListener('click', startHandler);
  } else {
    $window.removeEventListener('dre20:start', startHandler);
    start(mode, containerID, $window);
  }
}

/**
 * The usingDigitalDataField is used to determine if we are using the digital data field
 * @param {Object} $location - The location object
 * @returns {boolean} The using digital data field
 */
export function getUsingDigitalDataField($location) {
  const digitalDataField = $location.search.match(/digitalData=(0|1)/);
  const hasDigitalDataField = !!digitalDataField;
  let usingDigitalDataField = true;
  if (hasDigitalDataField) {
    usingDigitalDataField = !!parseInt(digitalDataField[1], 10);
  }
  return usingDigitalDataField;
}

/**
 * The dre20uxFlag is used to determine if we are using the dre 2.0 experience
 * @param {Object} $window - The window object
 * @param {Object} $location - The location object
 * @returns {boolean} The dre20ux flag
 */
export async function getDre20uxFlag($window, $location) {
  // check if we have digital data field in the query parms
  // if so we may or may not being using digital data
  const usingDigitalDataField = getUsingDigitalDataField($location);
 // assume we are using the flag
  let fulDre20uxFlag = true;

  // if we're using the digital data we'll query the digital data object
  // to see if it exists and wait up to 10 seconds for it load
  if (usingDigitalDataField) {
    await until(
      () => !!$window?.digitalData?.export()?.flag,
      { timeoutMs: 10000, intervalMs: 100 },
    );

    // since we found we'll grab the flag which controls the primary experience
    const digitalDataExportFlag = $window?.digitalData?.export()?.flag;
    fulDre20uxFlag = digitalDataExportFlag && digitalDataExportFlag['ful-dre-2-0-ux'];
  }

  return fulDre20uxFlag;
}

/**
 * The optimized field is used to determine what the experience is optimzed for
 * currently mfe is used primarly and legacy is used for the old experience
 * @param {Object} params - The parameters object
 * @returns {string|null} The optimized value or null if not found
 */
export function getOptimized(params) {
  if (typeof params.optimized === 'string') {
    const optimizedField = params.optimized.match(/((mfe)|(spa)|(legacy))/);
    return optimizedField?.length ? optimizedField[1] : null;
  }
  return null;
}

/**
 * The mode field is used to determine if we are using the mfe or spa
 * the mode should primarly be "mfe" which renders our experience as an mfe.
 * the spa mode is not currently being used.
 * @param {Object} params - The parameters object
 * @returns {string} The mode to use
 */
export function getMode(params) {
  return params.mode || 'mfe';
}

/**
 * The target field is used to determine what element in the dom
 * we'll render the experience into
 * @param {Object} params - The parameters object
 * @returns {string} The target to use
 */
export function getTarget(params) {
  return params.target || 'dre-mfe';
}

/**
 * The button field is used to determine what element in the dom
 * we'll attach a click handler to initate the page
 * @param {Object} $document - The document object
 * @param {Object} params - The parameters object
 * @returns {Element|null} The button to use or null if not found
 */
export function getTriggerButton($document, params) {
  const buttonField = decodeURIComponent(params.button || '.returns-view-toggle');
  return $document.querySelector(buttonField);
}

/**
 * The switch value is used to determine if we are to bootstrap the application 
 * @param {Object} $window - The window object
 * @param {string|null} optimized - The optimized value
 * @param {boolean} fulDre20uxFlag - The dre 2.0 flag
 * @param {boolean} usingLegacy - The using legacy flag
 * @returns {boolean} The switch value
 */
export function getSwitchValue($window, optimized, fulDre20uxFlag, usingLegacy) {
  return (
    // we have the flag
    ($window.hasDRE20 && !!fulDre20uxFlag)
    // we have an override
    || !!optimized
  ) && !usingLegacy
}

/**
 * The load function is the entry point for the application
 * it will determine what the experience is optimzed for
 * and bootstrap the application
 * @param {Object} global - The global object
 */
export async function load(global) {
  const {
    document: $document,
    location: $location,
    window: $window,
  } = global;

  const params = getParams($window);
  const fulDre20uxFlag = await getDre20uxFlag($window, $location);
  const optimized = getOptimized(params);
  const usingLegacy = optimized === 'legacy';
  const mode = getMode(params);
  const target = getTarget(params);
  const $button = getTriggerButton($document, params);
  const switchValue = getSwitchValue($window, optimized, fulDre20uxFlag, usingLegacy);
  $window.dre20client = getSocketClient(io, $window, $window.console);

  bootstrap(
    switchValue,
    target,
    $button,
    $window,
    mode,
  );
}

run(() => {
  load(global);
});
